import { MortgageLeadTassoErrorEnvelope } from '../../../services/api/mortgage-leads/mortgage-leads';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readEnvelope = (error: unknown): Partial<MortgageLeadTassoErrorEnvelope> =>
  isRecord(error) ? error : {};

export const getLeadTassoErrorStatus = (error: unknown): number | null =>
  readEnvelope(error).status ?? null;

export const getLeadTassoErrorMessage = (error: unknown): string | null => {
  const { message } = readEnvelope(error);
  return typeof message === 'string' ? message : null;
};

/**
 * `resolved`: the `Idempotency-Key` already produced a Mongo lead, the same
 * outcome as a `201`. `pending`: that original write is still in flight and
 * has not resolved yet. `null`: not a `409 Duplicate request` at all.
 */
export type LeadTassoDuplicateOutcome = 'resolved' | 'pending';

export const getLeadTassoDuplicateOutcome = (
  error: unknown
): LeadTassoDuplicateOutcome | null => {
  const envelope = readEnvelope(error);
  if (envelope.status !== 409) return null;
  return envelope.existingRequestId ? 'resolved' : 'pending';
};

const PERMISSION_DENIED_MESSAGE =
  'Non sei autorizzato a registrare una Lead Tasso.';

const DUPLICATE_PENDING_MESSAGE =
  'La richiesta precedente è ancora in elaborazione. I dati inseriti sono stati conservati: attendi qualche istante e riprova.';

export const GENERIC_RETRYABLE_MESSAGE =
  'Non è stato possibile registrare la Lead Tasso. I dati inseriti sono stati conservati: riprova.';

/**
 * Translates a failed submission into what the admin reads. `400` shows the
 * backend's own `message` because only the server knows which field it
 * rejected; every other case uses a fixed Italian sentence so the operator
 * never sees a raw backend payload or a stack trace.
 */
export const describeLeadTassoFailure = (error: unknown): string => {
  const status = getLeadTassoErrorStatus(error);

  if (status === 403) return PERMISSION_DENIED_MESSAGE;
  if (status === 400) return getLeadTassoErrorMessage(error) ?? GENERIC_RETRYABLE_MESSAGE;
  if (getLeadTassoDuplicateOutcome(error) === 'pending') {
    return DUPLICATE_PENDING_MESSAGE;
  }

  return GENERIC_RETRYABLE_MESSAGE;
};
